import { useMemo } from 'react';
import { Button, Drawer, FormNumberInput, Pagination } from '@/components/UI';
import { parseNumericDraft, sanitizeNumericDraft, toNumericDraft } from '@/components/UI/numericDraft';
import FormCheckbox from '@/components/UI/FormCheckbox';
import DataTable from '@/components/UI/DataTable';
import type { SortingState } from '@/components/UI/dataTableConfig';
import type { AuditCandidate, OptimizerFilterDef, OptimizerFilterKey, OptimizerJobRow, OptimizerResultRow } from './types';
import { OPTIMIZER_FILTER_GROUPS } from './constants';
import { buildResultColumns } from './resultColumns';
import { RAW_RETURN_SUM_DESCRIPTION, RAW_RETURN_SUM_LABEL } from './returnMetricCopy';

interface ResultsSectionProps {
    visibleFilters: OptimizerFilterDef[];
    activeFilters: OptimizerFilterDef[];
    filterValues: Record<OptimizerFilterKey, string>;
    setFilterValue: (key: OptimizerFilterKey, value: string) => void;
    clearFilters: () => void;
    filtersDrawerOpen: boolean;
    setFiltersDrawerOpen: (open: boolean) => void;
    holdoutFractionLabel: string;
    bundleSelectedResultIds: Set<number>;
    toggleBundleSelectedResultId: (resultId: number) => void;
    clearBundleSelection: () => void;
    selectedBundleResultIdsByRank: number[];
    openPromoteBundleModal: (resultIds: number[]) => void;
    hideSimilarResults: boolean;
    setHideSimilarResults: (value: boolean) => void;
    hideSimilarThreshold: string;
    setHideSimilarThreshold: (value: string) => void;
    hiddenSimilarCount: number;
    previewBundlePending: boolean;
    confirmBundlePending: boolean;
    onPreviewSelectedBundle: () => void;
    onConfirmSelectedBundle: () => void;
    suggestBundlesPending: boolean;
    onSuggestBundles: () => void;
    selectedJobId: number | null;
    selectedJobStatus: OptimizerJobRow['status'] | undefined;
    selectedJobOutcomeMode: OptimizerJobRow['outcomeMode'] | undefined;
    selectedJobUsesOptionEv: boolean;
    selectedJobUsesOptionMetric: boolean;
    replayConfirmationPendingResultId: number | null;
    results: OptimizerResultRow[];
    resultsFilteredForDiversity: OptimizerResultRow[];
    resultsIsError: boolean;
    resultsErrorMessage: string | undefined;
    resultsIsFetching: boolean;
    resultsPage: number;
    resultsPageSize: number;
    resultsTotalCount: number;
    resultsSorting: SortingState;
    setResultsPage: (page: number) => void;
    setResultsSorting: (sorting: SortingState) => void;
    displayIndexByResultId: Map<number, number>;
    auditCandidateByResultId: Map<number, AuditCandidate>;
    auditPackPending: boolean;
    openPromoteModal: (result: OptimizerResultRow) => void;
    onConfirmReplay: (result: OptimizerResultRow) => void;
    onShowRuleJson: (result: OptimizerResultRow) => void;
    onShowFolds: (resultId: number) => void;
}

const ResultsSection = ({
    visibleFilters,
    activeFilters,
    filterValues,
    setFilterValue,
    clearFilters,
    filtersDrawerOpen,
    setFiltersDrawerOpen,
    holdoutFractionLabel,
    bundleSelectedResultIds,
    toggleBundleSelectedResultId,
    clearBundleSelection,
    selectedBundleResultIdsByRank,
    openPromoteBundleModal,
    hideSimilarResults,
    setHideSimilarResults,
    hideSimilarThreshold,
    setHideSimilarThreshold,
    hiddenSimilarCount,
    previewBundlePending,
    confirmBundlePending,
    onPreviewSelectedBundle,
    onConfirmSelectedBundle,
    suggestBundlesPending,
    onSuggestBundles,
    selectedJobId,
    selectedJobStatus,
    selectedJobOutcomeMode,
    selectedJobUsesOptionEv,
    selectedJobUsesOptionMetric,
    replayConfirmationPendingResultId,
    results,
    resultsFilteredForDiversity,
    resultsIsError,
    resultsErrorMessage,
    resultsIsFetching,
    resultsPage,
    resultsPageSize,
    resultsTotalCount,
    resultsSorting,
    setResultsPage,
    setResultsSorting,
    displayIndexByResultId,
    auditCandidateByResultId,
    auditPackPending,
    openPromoteModal,
    onConfirmReplay,
    onShowRuleJson,
    onShowFolds
}: ResultsSectionProps) => {
    const resultColumns = useMemo(
        () =>
            buildResultColumns({
                bundleSelectedResultIds,
                toggleBundleSelectedResultId,
                displayIndexByResultId,
                auditCandidateByResultId,
                auditPackPending,
                selectedJobUsesOptionEv,
                selectedJobUsesOptionMetric,
                selectedJobOutcomeMode,
                replayConfirmationPendingResultId,
                openPromoteModal,
                onConfirmReplay,
                onShowRuleJson,
                onShowFolds
            }),
        [
            auditCandidateByResultId,
            auditPackPending,
            bundleSelectedResultIds,
            displayIndexByResultId,
            onShowFolds,
            onShowRuleJson,
            onConfirmReplay,
            openPromoteModal,
            replayConfirmationPendingResultId,
            selectedJobOutcomeMode,
            selectedJobUsesOptionEv,
            selectedJobUsesOptionMetric,
            toggleBundleSelectedResultId
        ]
    );

    return (
        <>
            {selectedJobOutcomeMode === 'fixed_horizon' && (
                <div className="mb-3 rounded border border-amber-700 bg-amber-900/30 px-3 py-2 text-xs text-amber-200">
                    <span className="font-semibold">Exploratory entry-edge proxy — not realized performance.</span> Fixed-horizon results
                    score how a signal resolves at a fixed horizon and{' '}
                    <span className="font-semibold">ignore the strategy exit (stops / targets / partials)</span>
                    {selectedJobUsesOptionMetric ? ' and real option legs' : ''}, so win rate and profit here routinely overstate what a
                    backtest realizes. Re-run in <span className="font-mono">strategy_exit</span> mode (or attach a strategy-exit
                    confirmation) before promoting.
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <Button size="sm" variant={activeFilters.length > 0 ? 'primary' : 'secondary'} onClick={() => setFiltersDrawerOpen(true)}>
                    <span className="inline-flex items-center gap-2">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M3 4h18M6 12h12M10 20h4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                        Filters
                        {activeFilters.length > 0 && (
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-semibold">
                                {activeFilters.length}
                            </span>
                        )}
                    </span>
                </Button>

                {activeFilters.map((def) => (
                    <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800/80 py-1 pl-2.5 pr-1.5 text-xs text-zinc-200"
                        key={def.key}
                    >
                        <span className="text-zinc-400">{def.chipLabel}</span>
                        <span className="font-medium text-white">
                            {def.comparator} {filterValues[def.key]}
                            {def.unit}
                        </span>
                        <button
                            aria-label={`Clear ${def.chipLabel} filter`}
                            className="rounded-full p-0.5 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                            type="button"
                            onClick={() => setFilterValue(def.key, '')}
                        >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                            </svg>
                        </button>
                    </span>
                ))}

                {activeFilters.length > 0 && (
                    <button
                        className="text-xs text-zinc-400 underline-offset-2 hover:text-white hover:underline"
                        type="button"
                        onClick={clearFilters}
                    >
                        Clear all
                    </button>
                )}
            </div>

            <Drawer
                footer={
                    <>
                        <Button size="sm" variant="secondary" onClick={clearFilters}>
                            Clear all
                        </Button>
                        <Button size="sm" variant="primary" onClick={() => setFiltersDrawerOpen(false)}>
                            Done
                        </Button>
                    </>
                }
                isOpen={filtersDrawerOpen}
                title="Filter results"
                width="md"
                onClose={() => setFiltersDrawerOpen(false)}
            >
                <p className="mb-4 text-xs text-zinc-500">
                    Internal holdout trades = how many signals fired in the reserved tail of the training window. Filtering by holdout
                    trades helps avoid tiny-sample winners. Training CV/holdout metrics are winsorized; raw return metrics are not.
                </p>
                <div className="flex flex-col gap-6">
                    {OPTIMIZER_FILTER_GROUPS.map((group) => {
                        const groupFilters = visibleFilters.filter((def) => def.group === group.key);
                        if (groupFilters.length === 0) return null;

                        return (
                            <div key={group.key}>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{group.title}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {groupFilters.map((def) => (
                                        <FormNumberInput
                                            helpText={
                                                typeof def.helpText === 'function' ? def.helpText({ holdoutFractionLabel }) : def.helpText
                                            }
                                            id={`opt-results-${def.key}`}
                                            integer={def.normalize.integer === true}
                                            key={def.key}
                                            label={def.label}
                                            max={def.inputMax}
                                            min={def.inputMin}
                                            value={parseNumericDraft(filterValues[def.key])}
                                            onValueChange={(value) => setFilterValue(def.key, toNumericDraft(value))}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Drawer>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                <div className="text-zinc-500 text-xs">
                    Selected for bundle: <span className="text-white">{bundleSelectedResultIds.size.toLocaleString()}</span>
                    {hideSimilarResults && bundleSelectedResultIds.size > 0 && (
                        <>
                            {' '}
                            · Hidden similar: <span className="text-white">{hiddenSimilarCount.toLocaleString()}</span>
                        </>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <FormCheckbox
                        checked={hideSimilarResults}
                        id="opt-hide-similar-results"
                        label="Hide similar"
                        labelClassName="text-zinc-400 text-xs font-normal"
                        onChange={(e) => setHideSimilarResults(e.target.checked)}
                    />
                    <div className="flex items-center gap-2">
                        <div className="text-zinc-500 text-xs whitespace-nowrap">Similarity ≥</div>
                        <input
                            className="bg-[#1E1E1E] border border-[#444] rounded-md px-2 py-1 text-zinc-300 w-20 focus:outline-none focus:ring-1 focus:ring-[#888] focus:border-[#888] h-[32px] text-xs disabled:opacity-50"
                            disabled={!hideSimilarResults}
                            inputMode="decimal"
                            type="text"
                            value={hideSimilarThreshold}
                            onChange={(e) =>
                                setHideSimilarThreshold(sanitizeNumericDraft(e.target.value, { integer: false, allowNegative: false }))
                            }
                        />
                    </div>
                    <Button
                        disabled={bundleSelectedResultIds.size < 2 || previewBundlePending}
                        size="sm"
                        variant="secondary"
                        onClick={onPreviewSelectedBundle}
                    >
                        {previewBundlePending ? 'Previewing…' : 'Preview OR bundle'}
                    </Button>
                    <Button
                        disabled={bundleSelectedResultIds.size < 2 || confirmBundlePending}
                        size="sm"
                        variant="secondary"
                        onClick={onConfirmSelectedBundle}
                    >
                        {confirmBundlePending ? 'Confirming…' : 'Confirm OR bundle'}
                    </Button>
                    <Button
                        disabled={bundleSelectedResultIds.size < 2}
                        size="sm"
                        variant="primary"
                        onClick={() => openPromoteBundleModal(selectedBundleResultIdsByRank)}
                    >
                        Create OR bundle
                    </Button>
                    <Button
                        disabled={suggestBundlesPending || selectedJobId === null}
                        size="sm"
                        variant="secondary"
                        onClick={onSuggestBundles}
                    >
                        {suggestBundlesPending ? 'Suggesting…' : 'Suggest bundles'}
                    </Button>
                    <Button disabled={bundleSelectedResultIds.size === 0} size="sm" variant="secondary" onClick={clearBundleSelection}>
                        Clear selection
                    </Button>
                </div>
            </div>
            <div className="mb-3 text-xs text-zinc-500">
                <span className="font-medium text-zinc-300">{RAW_RETURN_SUM_LABEL}:</span> {RAW_RETURN_SUM_DESCRIPTION}
            </div>
            {resultsIsError && (
                <div className="text-red-300 text-sm mb-2">Failed to load results: {resultsErrorMessage ?? 'Unknown error'}</div>
            )}
            {resultsIsFetching && results.length === 0 ? (
                <div className="text-zinc-400 text-sm">Loading results…</div>
            ) : results.length === 0 && !resultsIsError ? (
                <div className="text-zinc-400 text-sm">
                    {selectedJobStatus === 'optimizing' || selectedJobStatus === 'validating'
                        ? 'Discovering results…'
                        : 'No results found yet.'}
                </div>
            ) : results.length > 0 ? (
                <>
                    <DataTable
                        allowOverflow
                        manualSorting
                        columns={resultColumns}
                        data={resultsFilteredForDiversity}
                        sorting={resultsSorting}
                        onSortingChange={setResultsSorting}
                    />
                    <div className="mt-3 flex flex-col gap-2 text-xs text-zinc-500 md:flex-row md:items-center md:justify-between">
                        <span>
                            Showing {((resultsPage - 1) * resultsPageSize + 1).toLocaleString()}–
                            {Math.min(resultsPage * resultsPageSize, resultsTotalCount).toLocaleString()} of{' '}
                            {resultsTotalCount.toLocaleString()}
                            {hideSimilarResults && hiddenSimilarCount > 0
                                ? ` (${hiddenSimilarCount.toLocaleString()} similar hidden on this page)`
                                : ''}
                        </span>
                        <Pagination
                            currentPage={resultsPage}
                            disabled={resultsIsFetching}
                            itemsPerPage={resultsPageSize}
                            totalItems={resultsTotalCount}
                            onPageChange={setResultsPage}
                        />
                    </div>
                </>
            ) : null}
        </>
    );
};

export default ResultsSection;
